import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

const PROVINCES = [
  '北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江',
  '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南',
  '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州',
  '云南', '西藏', '陕西', '甘肃', '青海', '宁夏', '新疆', '香港', '澳门', '台湾',
];

export default function KycPage() {
  const [form, setForm] = useState({ name: '', id: '', region: '' });
  const [status, setStatus] = useState<'approved' | 'pending' | 'rejected' | undefined>(undefined);
  const [rewards, setRewards] = useState<{ stable_coin: number; stock: number } | null>(null);
  const [createdAt, setCreatedAt] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/kyc/status').then(({ data: res }) => { setStatus(res.status as any); if (res.data) { setForm({ name: res.data.name ?? '', id: res.data.id_number ?? '', region: res.data.region ?? '' }); setCreatedAt(res.data.created_at); } }).catch(() => {});
    api.get('/kyc/rewards').then(({ data }) => setRewards(data)).catch(() => {});
  }, []);

  const maskedId = useMemo(() => { const v = form.id || ''; if (v.length <= 8) return v; const head = v.slice(0, 3); const tail = v.slice(-4); return `${head}${'*'.repeat(Math.max(5, v.length - 7))}${tail}`; }, [form.id]);

  const locked = status === 'approved' || status === 'pending';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (locked) { toast.success(status === 'approved' ? '已通过实名认证' : '审核中，请稍后再试'); return; }
    if (!form.name || !form.id) { toast.error('请完整填写信息'); return; }
    if (!/^\d{6}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/.test(form.id)) { toast.error('请输入正确的身份证号码'); return; }
    if (!form.region) { toast.error('请选择所在地区'); return; }
    try { await api.put('/profile', { name: form.name }); } catch {}
    try {
      await api.post('/kyc', { name: form.name, id: form.id, region: form.region, address: '' });
      toast.success('实名认证已提交');
      navigate('/profile');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || '提交失败');
    }
  }

  return (
    <div
      className="max-w-md mx-auto min-h-screen bg-[url(/images/heshi_beijng.png)] bg-[length:100%_100%] bg-no-repeat bg-top pb-[100px]" 
    >
      <AuthGuard />

      {/* Back button */}
      <div className="pt-4 px-4">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Form card */}
      <div className="px-4 mt-[350px] relative z-10 bg-[url(/images/heshi_kuang.png)] bg-[length:100%_100%] bg-no-repeat rounded-xl p-4 mx-4 pb-[120px] pt-[50px]">
        <div className="relative">   
            <form onSubmit={submit} className="flex-1 flex flex-col gap-3">
              {/* Name */}
              <div className="flex items-center gap-2">
                <span className="text-black text-xs w-16 flex-shrink-0">真实姓名</span>
                <div className="relative">
                  <img src="/images/heshi_shuru.png" alt="" className="w-full h-11 object-fill" />
                  <div className="absolute inset-0 flex items-center px-4">
                    <input
                      className="flex-1 text-sm bg-transparent outline-none placeholder:text-white/40"
                      placeholder="请输入真实姓名"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value.replace(/\s/g, '') })}
                      disabled={locked}
                    />
                  </div>
                </div>
              </div>

              {/* ID number */}
              <div className="flex items-center gap-2">
                <span className="text-black text-xs w-16 flex-shrink-0">证件号码</span>
                <div className="relative">
                  <img src="/images/heshi_shuru.png" alt="" className="w-full h-11 object-fill" />
                  <div className="absolute inset-0 flex items-center px-4">
                    <input
                      className="flex-1 text-sm bg-transparent outline-none placeholder:text-white/40"
                      placeholder="请输入证件号码"
                      maxLength={18}
                      value={status === 'approved' ? maskedId : form.id}
                      onChange={e => setForm({ ...form, id: e.target.value.replace(/[^\dXx]/gi, '').slice(0, 18) })}
                      disabled={locked}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-black text-xs w-16 flex-shrink-0">所在地区</span>
                <div className="relative">
                  <img src="/images/heshi_shuru.png" alt="" className="w-full h-11 object-fill" />
                  <select
                    className="absolute inset-0 w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none disabled:text-gray-500 disabled:bg-gray-100 appearance-none"
                    value={form.region}
                    onChange={e => setForm({ ...form, region: e.target.value })}
                    disabled={status === 'approved' || status === 'pending'}
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  >
                    <option value="">请选择所在地区</option>
                    {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
 
              {/* Submit button */}
              <button type="submit" className="w-full mt-2" disabled={locked}>
                <img
                  src="/images/heshi_lijitijiao.png"
                  alt="立即提交"
                  className={`w-full ${locked ? 'opacity-50' : ''}`}
                />
              </button>
            </form> 
        </div>
      </div>
    </div>
  );
}
